import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  faBuilding,
  faBoxesStacked,
  faAddressBook,
  faUsers,
  faIdBadge,
  faCog,
  faCreditCard,
  faSliders,
  faCodeBranch,
  faCashRegister,
  faBuildingColumns,
  faCoins,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../AppContext';
import mainAxios from '../../services/mainAxios';
import SettingsCard from './SettingsCard';
import SettingsBreadcrumb from './SettingsBreadcrumb';
import Loader from '../Layout/Loader';
import './settings.scss';

const Settings = () => {
  const { t } = useTranslation('translations');
  const history = useHistory();
  const location = useLocation();
  const {
    user: [user],
    company: [currentCompany],
  } = useAppContext();

  // Get companyId from URL query params
  const searchParams = new URLSearchParams(location.search);
  const companyIdParam = searchParams.get('companyId');

  const [managedCompany, setManagedCompany] = useState(null);
  const [loading, setLoading] = useState(false);

  // Determine if we're in admin mode (managing a specific company)
  const isAdminMode = !!companyIdParam;

  // Fetch the company data if companyId is provided and different from current
  useEffect(() => {
    if (companyIdParam) {
      if (currentCompany?.id == companyIdParam) {
        setManagedCompany(currentCompany);
      } else {
        setLoading(true);
        mainAxios
          .post('apiEndpoint/search', {
            object: 'GetCompany',
            value: '',
            params: {
              id: companyIdParam,
            },
          })
          .then((res) => {
            if (res.data.status && res.data.content?.length > 0) {
              setManagedCompany(res.data.content[0]);
            }
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    } else {
      setManagedCompany(null);
    }
  }, [companyIdParam, currentCompany]);

  const userRoleId = user?.role?.id;
  const badgeCompany = isAdminMode ? managedCompany : currentCompany;

  const settingsCategories = [
    {
      id: 'general-info',
      title: t('settings.companyInfo'),
      icon: faBuilding,
      description: t('settings.generalInfoDesc'),
      path: '/settings/general-info',
      allowedRoles: [1, 2, 3],
    },
    {
      id: 'branches',
      title: t('company.branches'),
      icon: faCodeBranch,
      description: t('settings.branchesDesc'),
      path: '/settings/branches',
      allowedRoles: [1, 2],
    },
    {
      id: 'tcr',
      title: 'TCR',
      icon: faCashRegister,
      description: t('settings.tcrDesc'),
      path: '/settings/tcr',
      allowedRoles: [1, 2],
    },
    {
      id: 'bank-accounts',
      title: t('company.bankAccounts'),
      icon: faBuildingColumns,
      description: t('settings.bankAccountsDesc'),
      path: '/settings/bank-accounts',
      allowedRoles: [1, 2, 3],
    },
    {
      id: 'products-catalog',
      title: t('settings.productsCatalog'),
      icon: faBoxesStacked,
      description: t('settings.productsCatalogDesc'),
      path: '/settings/products',
      allowedRoles: [1, 2, 3, 4, 5, 6, 7],
    },
    {
      id: 'contacts',
      title: t('mySuppliers.contacts'),
      icon: faAddressBook,
      description: t('settings.contactsDesc'),
      path: '/settings/contacts',
      allowedRoles: [1, 2, 3, 4],
    },
    {
      id: 'users-access',
      title: t('navbar.users'),
      icon: faUsers,
      description: t('settings.usersAccessDesc'),
      path: '/settings/users',
      allowedRoles: [1],
    },
    {
      id: 'operators-cis',
      title: t('navbar.operatorsCis'),
      icon: faIdBadge,
      description: t('settings.operatorsCisDesc'),
      path: '/settings/operators',
      allowedRoles: [1, 2],
    },
    {
      id: 'preferences',
      title: t('settings.preferences'),
      icon: faSliders,
      description: t('settings.preferencesDesc'),
      path: '/settings/preferences',
      allowedRoles: [1, 2, 3],
    },
    {
      id: 'currencies',
      title: t('navbar.currencies'),
      icon: faCoins,
      description: t('settings.currenciesDesc'),
      path: '/settings/currencies',
      allowedRoles: [1],
    },
    {
      id: 'subscription',
      title: t('navbar.subscriptions'),
      icon: faCreditCard,
      description: t('settings.subscriptionDesc'),
      path: '/settings/subscription',
      allowedRoles: [1],
    },
    {
      id: 'tools',
      title: t('settings.tools'),
      icon: faCog,
      description: t('settings.toolsDesc'),
      path: '/settings/tools',
      allowedRoles: [1, 2, 3, 4, 5],
    },
    {
      id: 'logs',
      title: t('navbar.logs'),
      icon: faClipboardList,
      description: t('settings.logsDesc'),
      path: '/settings/logs',
      allowedRoles: [1, 2],
    },
  ];

  const visibleCategories = settingsCategories.filter(
    (category) => category.allowedRoles.includes(userRoleId)
  );

  const handleCardClick = (path) => {
    // Preserve companyId in URL when navigating to sub-pages
    if (companyIdParam) {
      history.push(`${path}?companyId=${companyIdParam}`);
    } else {
      history.push(path);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="settings-page">
      {/* Breadcrumb for admin mode */}
      {isAdminMode && managedCompany && (
        <SettingsBreadcrumb
          items={[]}
          company={managedCompany}
          isAdminMode={isAdminMode}
        />
      )}

      <div className="settings-page__header">
        <h2>{t('navbar.settings')}</h2>
        {badgeCompany && (
          <span className="settings-subpage__company-badge">
            {badgeCompany.name}
          </span>
        )}
      </div>
      <div className="settings-page__grid">
        {visibleCategories.map((category) => (
          <SettingsCard
            key={category.id}
            title={category.title}
            icon={category.icon}
            description={category.description}
            onClick={() => handleCardClick(category.path)}
          />
        ))}
      </div>
    </div>
  );
};

export default Settings;
